import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useModalControls} from 'hooks';
import {NotificationModal} from 'components/NotificationModal';
import {StatusText} from 'components/StatusText';
import {
  NavigationTitle,
  NavigationContainer,
  NavigationItem as NavigationItemWrapper,
  NavigationTexts,
  NextArrow,
  NavigationDescription,
  NavigationIconContainer,
} from './styled';
import {Analytics} from 'analytics';

type NavigationNotificationProps = {
  title: string;
  message: string | JSX.Element;
};

type NavigationItemProps = {
  testId?: string;
  title: string;
  completed?: boolean;
  path: string | null;
  icon?: JSX.Element;
  isStatusBoxVisible?: boolean;
  description?: string;
  maxWidth?: string;
  flat?: boolean;
  notification?: NavigationNotificationProps;
};
const NavigationItem = ({
  testId,
  title,
  icon,
  completed,
  path,
  isStatusBoxVisible = true,
  description,
  maxWidth,
  flat,
  notification,
}: NavigationItemProps) => {
  const {
    isOpen: isOpenNotificationModal,
    closeModal: closeNotificationModal,
    openModal: openNotificationModal,
  } = useModalControls();
  const navigate = useNavigate();

  const goToView = React.useCallback(() => {
    Analytics.track('Navigation Item goToView', {
      path,
    });
    if (path) navigate(`../${path}`);
  }, [navigate, path]);

  const clickHandler = React.useCallback(() => {
    Analytics.track('Navigation Item Clicked', {
      notification: notification?.title,
    });
    if (notification) {
      openNotificationModal();
    } else {
      goToView();
    }
  }, [goToView, openNotificationModal, notification]);

  return (
    <NavigationContainer
      $maxWidth={maxWidth}
      $flat={flat}
      className="nav-item navigation-item-container"
      onClick={clickHandler}
      $completed={completed}
      data-testid={testId}
    >
      <NavigationItemWrapper $isClicked={!!path}>
        <NavigationIconContainer className="navigation-item-container__icon-container">
          <div className="w-10 h-10 rounded-full flex items-center justify-center [background-color:var(--background-secondary)]">
            {icon}
          </div>
        </NavigationIconContainer>
        <NavigationTexts className="nav-item__content">
          <NavigationTitle className="nav-item__title">{title}</NavigationTitle>
          {isStatusBoxVisible && <StatusText completed={!!completed} />}
          {description && (
            <NavigationDescription className="nav-item__description">
              {description}
            </NavigationDescription>
          )}
        </NavigationTexts>
      </NavigationItemWrapper>
      <NextArrow name="grayRightArrow" />
      {notification && (
        <NotificationModal
          title={notification?.title}
          isOpenModalSheet={isOpenNotificationModal}
          onClose={goToView}
          text={notification?.message}
          withModalMobileCloseButton={false}
          closeOnDocumentClick={false}
          onConfirm={() => {
            closeNotificationModal();
            goToView();
          }}
        />
      )}
    </NavigationContainer>
  );
};

export {NavigationItem};
